import { message } from 'antd';
import html2canvas from 'html2canvas';
import dayjs from 'dayjs';

/**
 * 导出图片的自定义 Hook
 * @param filenamePrefix 文件名前缀
 * @returns 导出函数
 */
export const useExportImage = (filenamePrefix: string = '报告') => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    const exportImage = async (
        elementSelector: string,
        options?: {
            beforeCapture?: () => void | Promise<void>;
            afterCapture?: () => void | Promise<void>;
        }
    ) => {
        try {
            message.loading({ content: '正在生成图片，请稍后...', key: 'export', duration: 0 });

            if (options?.beforeCapture) {
                await options.beforeCapture();
            }

            const element = document.querySelector(elementSelector) as HTMLElement;
            if (!element) {
                throw new Error('找不到报告内容');
            }

            // 添加导出专用样式类，确保页面上的懒加载/导出态内容先渲染出来
            element.classList.add('pdf-export-mode');

            // iOS 需要更长的等待时间
            await new Promise(resolve => setTimeout(resolve, isMobile ? 500 : 300));

            // 获取元素实际尺寸，避免把当前页面偏移和响应式布局一起截进去
            const rect = element.getBoundingClientRect();
            const actualWidth = Math.ceil(Math.max(element.scrollWidth, rect.width));

            const exportWrapper = document.createElement('div');
            const exportElement = element.cloneNode(true) as HTMLElement;
            exportElement.classList.add('pdf-export-mode');
            exportElement.setAttribute('data-export-root', 'true');

            exportWrapper.style.cssText = `
                position: fixed;
                left: -100000px;
                top: 0;
                width: ${actualWidth}px;
                padding: 0;
                margin: 0;
                background: #ffffff;
                overflow: visible;
                z-index: -1;
                pointer-events: none;
                opacity: 0;
            `;

            exportElement.style.width = `${actualWidth}px`;
            exportElement.style.maxWidth = 'none';
            exportElement.style.margin = '0';
            exportElement.style.transform = 'none';
            exportElement.style.animation = 'none';

            exportWrapper.appendChild(exportElement);
            document.body.appendChild(exportWrapper);

            // 强制触发重排，确保导出样式命中克隆节点
            void exportElement.offsetHeight;
            await new Promise(resolve => setTimeout(resolve, 50));

            const actualHeight = Math.ceil(Math.max(exportElement.scrollHeight, exportElement.offsetHeight));

            console.log('[导出] 元素尺寸:', { width: rect.width, height: rect.height, actualWidth, actualHeight });

            // 使用 html2canvas 将内容转为图片
            let canvas: HTMLCanvasElement;
            try {
                canvas = await html2canvas(exportElement, {
                    scale: isMobile ? 1.5 : 2,
                    useCORS: true,
                    logging: true,
                    allowTaint: false,
                    backgroundColor: '#ffffff',
                    width: actualWidth,
                    height: actualHeight,
                    windowWidth: actualWidth,
                    windowHeight: actualHeight,
                    scrollX: 0,
                    scrollY: 0,
                    removeContainer: true,
                    imageTimeout: 15000,
                    onclone: (clonedDoc) => {
                        const clonedElement = clonedDoc.querySelector('[data-export-root="true"]') as HTMLElement;
                        if (clonedElement) {
                            clonedElement.classList.add('pdf-export-mode');
                            clonedElement.style.position = 'relative';
                            clonedElement.style.left = '0';
                            clonedElement.style.top = '0';
                            clonedElement.style.margin = '0';
                            clonedElement.style.transform = 'none';
                            clonedElement.style.animation = 'none';
                            clonedElement.style.width = `${actualWidth}px`;
                            clonedElement.style.maxWidth = 'none';
                            clonedElement.style.background = '#ffffff';
                        }
                    }
                });
            } finally {
                document.body.removeChild(exportWrapper);
            }

            console.log('[导出] Canvas 生成成功, 尺寸:', canvas.width, 'x', canvas.height);

            // 移除导出样式类
            element.classList.remove('pdf-export-mode');

            const imgData = canvas.toDataURL('image/jpeg', 0.9);
            const date = dayjs().format('YYYY-MM-DD');

            // 移动端处理：提供预览
            if (isMobile) {
                showMobilePreview(imgData, date, filenamePrefix);
                message.success({ content: '图片已生成，请长按保存', key: 'export', duration: 3 });
            } else {
                // PC 端处理：直接下载
                const link = document.createElement('a');
                link.href = imgData;
                link.download = `${filenamePrefix}_${date}.jpg`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                message.success({ content: '图片保存成功！', key: 'export', duration: 2 });
            }
        } catch (error) {
            console.error('[导出] 详细错误:', error);
            console.error('[导出] 错误堆栈:', (error as Error).stack);

            message.error({ content: '图片保存失败，请重试', key: 'export', duration: 2 });

            const element = document.querySelector(elementSelector) as HTMLElement;
            if (element) {
                element.classList.remove('pdf-export-mode');
            }
        } finally {
            if (options?.afterCapture) {
                await options.afterCapture();
            }
        }
    };

    // 移动端预览
    const showMobilePreview = (imgData: string, date: string, prefix: string) => {
        const isAndroid = /Android/i.test(navigator.userAgent);

        // 创建遮罩层（可滚动）
        const overlay = document.createElement('div');
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.92);
            z-index: 9998;
            overflow-y: auto;
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
        `;

        // 顶部提示栏
        const header = document.createElement('div');
        header.style.cssText = `
            position: sticky;
            top: 0;
            left: 0;
            right: 0;
            background: linear-gradient(180deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.8) 70%, transparent 100%);
            padding: 16px 20px 40px;
            z-index: 10001;
            text-align: center;
        `;
        header.innerHTML = `
            <div style="color: #ffd700; font-size: 16px; font-weight: 600; margin-bottom: 6px;">
                📱 长按下方图片保存到相册
            </div>
            <div style="color: rgba(255,255,255,0.7); font-size: 13px;">
                可上下滑动查看完整内容
            </div>
        `;

        // 图片容器
        const imgWrapper = document.createElement('div');
        imgWrapper.style.cssText = `
            padding: 0 16px 100px;
            display: flex;
            justify-content: center;
        `;

        // 图片
        const img = new Image();
        img.src = imgData;
        img.style.cssText = `
            max-width: 100%;
            width: 420px;
            height: auto;
            box-shadow: 0 10px 40px rgba(0,0,0,0.5);
            border-radius: 8px;
            background: white;
            display: block;
        `;

        imgWrapper.appendChild(img);

        // 底部按钮栏
        const footer = document.createElement('div');
        footer.style.cssText = `
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background: linear-gradient(0deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.8) 70%, transparent 100%);
            padding: 30px 20px 24px;
            z-index: 10001;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 12px;
        `;

        // 安卓下载按钮
        if (isAndroid) {
            const downloadBtn = document.createElement('button');
            downloadBtn.textContent = '直接下载图片';
            downloadBtn.style.cssText = `
                background: linear-gradient(135deg, #d4af6a 0%, #c19b5a 100%);
                color: white;
                border: none;
                padding: 14px 40px;
                border-radius: 25px;
                font-size: 15px;
                font-weight: 600;
                cursor: pointer;
                box-shadow: 0 4px 15px rgba(212, 175, 106, 0.4);
                width: 100%;
                max-width: 280px;
            `;
            downloadBtn.onclick = () => {
                const link = document.createElement('a');
                link.href = imgData;
                link.download = `${prefix}_${date}.jpg`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                message.success({ content: '正在尝试下载...', key: 'export', duration: 2 });
            };
            footer.appendChild(downloadBtn);
        }

        // 关闭按钮
        const closeBtn = document.createElement('button');
        closeBtn.textContent = '关闭预览';
        closeBtn.style.cssText = `
            background: rgba(255, 255, 255, 0.12);
            color: white;
            border: 1px solid rgba(255, 255, 255, 0.25);
            padding: 12px 32px;
            border-radius: 20px;
            font-size: 14px;
            cursor: pointer;
            width: 100%;
            max-width: 280px;
        `;
        footer.appendChild(closeBtn);

        const closePreview = () => {
            document.body.removeChild(overlay);
        };

        closeBtn.addEventListener('click', closePreview);
        overlay.appendChild(header);
        overlay.appendChild(imgWrapper);
        overlay.appendChild(footer);
        document.body.appendChild(overlay);
    };

    return { exportImage, isMobile };
};

export default useExportImage;
